import React, { useEffect, useState } from "react";
import { Button, TextField, Tooltip } from "@mui/material";
import useAxios from "../../helpers/Hooks/useAxios";
import { URLS } from "../../helpers/URLS";
import { toast } from "react-toastify";
import NoData from "../../loaders/NoData";
import SkeletonLoader from "../../loaders/Skeleton";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import moment from "moment";
import PersonAvatar from "../../helpers/PersonAvatar";
import RatingInput from "./RatingInput";
import { STATUS, byCategory, scoreTone } from "./helpers";
import "./Performance.scss";

const TeamReviews = () => {

    const { fetchData } = useAxios();

    const [loading, setLoading] = useState(true);
    const [reviews, setReviews] = useState([]);
    const [openId, setOpenId] = useState(null);
    const [draft, setDraft] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        const resp = await fetchData({ url: URLS.reviewTeam, method: "get" });
        setLoading(false);
        if (resp?.success === false) {
            toast.error(resp?.message || "Could not load your team's reviews");
            return;
        }
        setReviews(resp.data || []);
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const open = (review) => {
        setOpenId(review._id);
        setDraft({
            items: review.items.map((i) => ({
                managerRating: i.managerRating || i.selfRating || 0,
                managerComment: i.managerComment || "",
            })),
            managerComment: review.managerComment || "",
        });
    };

    /* the address is on their employee record — the caller never supplies one */
    const remind = async (review) => {
        if (busy) return;
        setBusy(true);
        const resp = await fetchData({
            url: `${URLS.reviewRemind}/${review._id}`,
            method: "post",
        });
        setBusy(false);

        if (resp?.success === false) {
            toast.error(resp?.message || "Could not send the reminder");
            return;
        }
        toast.success(resp?.message || "Reminder sent");
        load();
    };

    const save = async (review, submit) => {
        if (busy) return;
        setBusy(true);
        const resp = await fetchData({
            url: `${URLS.reviewTeam}/${review._id}`,
            method: "put",
            body: { ...draft, submit },
        });
        setBusy(false);

        if (resp?.success === false) {
            toast.error(resp?.message || "Could not save");
            return;
        }
        toast.success(resp?.message || "Saved");
        if (submit) { setOpenId(null); setDraft(null); }
        load();
    };

    if (loading) return <SkeletonLoader />;

    if (!reviews.length) {
        return <NoData
            title="Nothing to assess"
            message="Reviews from the people who report to you appear here once they have submitted." />;
    }

    const waiting = reviews.filter((r) => r.status === "self_submitted").length;

    return (
        <div className="perf-page">
            {waiting > 0 && (
                <div className="perf-bar">
                    <b>{waiting}</b> {waiting === 1 ? "review is" : "reviews are"} waiting on you
                </div>
            )}

            {reviews.map((review) => {
                const editing = openId === review._id && review.status === "self_submitted";
                const meta = STATUS[review.status] || STATUS.pending;

                return (
                    <div className="wh-panel perf-review" key={review._id}>
                        <div className="perf-review-head">
                            <div className="wh-person perf-who">
                                <PersonAvatar name={review.employeeName} pic={review.profilePic} />
                                <div className="perf-who-text">
                                    <h6>{review.employeeName}</h6>
                                    <span>{review.designation || review.employeeCode} · {review.cycle?.name}</span>
                                </div>
                            </div>

                            <span className={`wh-pill ${meta.tone}`}>{meta.label}</span>

                            {review.selfScore > 0 && (
                                <span className={`wh-pill ${scoreTone(review.selfScore)}`}>
                                    Self {review.selfScore}/5
                                </span>
                            )}
                            {review.status === "completed" && (
                                <span className={`wh-pill ${scoreTone(review.managerScore)} perf-score`}>
                                    Final {review.managerScore}/5
                                </span>
                            )}

                            {/* nothing to assess until they submit — nudge them instead */}
                            {review.status === "pending" && (
                                <Tooltip
                                    title={review.remindedAt
                                        ? `Last reminded ${moment(review.remindedAt).format("DD MMM, h:mm A")}`
                                        : `Email ${review.employeeName} about their self assessment`}
                                    placement="top" arrow>
                                    <span>
                                        <Button variant="outlined" size="small" disabled={busy}
                                            startIcon={<NotificationsActiveOutlinedIcon />}
                                            onClick={() => remind(review)}>
                                            {review.remindedAt ? "Remind again" : "Send reminder"}
                                        </Button>
                                    </span>
                                </Tooltip>
                            )}

                            {review.status === "self_submitted" && !editing && (
                                <Button variant="contained" onClick={() => open(review)}>Assess</Button>
                            )}
                        </div>

                        {(editing || review.status === "completed") && (
                            <div className="perf-body">
                                {byCategory(review.items).map((group) => (
                                    <div className="perf-group" key={group.name}>
                                        <div className="perf-group-head">
                                            <h6>{group.name}</h6>
                                            {group.weight > 0 && <span className="wh-pill wh-pill-quiet">{group.weight}%</span>}
                                        </div>

                                        {group.rows.map((row) => (
                                            <div className="perf-kra" key={row.index}>
                                                <div className="perf-kra-text">
                                                    <b>{row.kra}</b>
                                                    {row.metric && <em>{row.metric}</em>}
                                                </div>

                                                {/* what they said about themselves, for context */}
                                                <div className="perf-kra-side">
                                                    <span className="perf-kra-label">They rated</span>
                                                    <RatingInput value={row.selfRating} readOnly
                                                        label={`self-${row.index}`} />
                                                    <p className="perf-said">
                                                        {row.selfComment || <em>No comment</em>}
                                                    </p>
                                                </div>

                                                <div className="perf-kra-manager">
                                                    <span className="perf-kra-label">Your rating</span>
                                                    <RatingInput
                                                        label={`mgr-${row.index}`}
                                                        value={editing ? draft.items[row.index].managerRating : row.managerRating}
                                                        readOnly={!editing}
                                                        onChange={(next) => {
                                                            const items = [...draft.items];
                                                            items[row.index] = { ...items[row.index], managerRating: next };
                                                            setDraft({ ...draft, items });
                                                        }}
                                                    />

                                                    {editing
                                                        ? <TextField
                                                            fullWidth multiline minRows={2} size="small"
                                                            label="Your feedback"
                                                            value={draft.items[row.index].managerComment}
                                                            onChange={(e) => {
                                                                const items = [...draft.items];
                                                                items[row.index] = { ...items[row.index], managerComment: e.target.value };
                                                                setDraft({ ...draft, items });
                                                            }} />
                                                        : <p className="perf-said">
                                                            {row.managerComment || <em>No comment</em>}
                                                        </p>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ))}

                                {review.selfComment && (
                                    <div className="perf-quote">
                                        <span>{review.employeeName} said</span>
                                        <p>{review.selfComment}</p>
                                    </div>
                                )}

                                <div className="perf-overall">
                                    {editing
                                        ? <TextField
                                            fullWidth multiline minRows={3} size="small"
                                            label="Overall feedback"
                                            placeholder="What went well, and what to focus on next quarter"
                                            value={draft.managerComment}
                                            onChange={(e) => setDraft({ ...draft, managerComment: e.target.value })} />
                                        : review.managerComment && (
                                            <div className="perf-quote perf-quote--manager">
                                                <span>Your feedback</span>
                                                <p>{review.managerComment}</p>
                                            </div>
                                        )}
                                </div>

                                {editing && (
                                    <div className="perf-actions">
                                        <Button variant="outlined" startIcon={<SaveRoundedIcon />}
                                            disabled={busy} onClick={() => save(review, false)}>
                                            Save draft
                                        </Button>
                                        <Button variant="contained" startIcon={<DoneAllRoundedIcon />}
                                            disabled={busy} onClick={() => save(review, true)}>
                                            Complete review
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default TeamReviews;
